/**
 * ReportContentModal — DMCA-style content-flagging modal.
 *
 * Open it with `<ReportContentModal refId={...} refTitle={...}
 * open={open} onClose={...} />`. Submits to `POST /api/content/{refId}/report`.
 *
 * The originality acknowledgement on the reported content (if any) is
 * surfaced in the admin queue — not here — so the reporter just picks a
 * reason and types a short explanation.
 */
import React, { useState } from 'react';
import { Flag, X, Loader2, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch, checkAuth } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
  { id: 'copyright', label: 'Copyright violation', hint: 'Uses my work or someone else\'s without permission' },
  { id: 'plagiarism', label: 'Plagiarism', hint: 'Copy-pasted from another author' },
  { id: 'ai_unedited', label: 'AI-generated, not edited', hint: 'Looks like raw LLM output' },
  { id: 'harmful', label: 'Harmful / inappropriate', hint: 'Hateful, harassing, NSFW outside the Vault' },
  { id: 'other', label: 'Other', hint: '' },
];

const ReportContentModal = ({ refId, refTitle, open, onClose }) => {
  const [reason, setReason] = useState('copyright');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const reset = () => {
    setReason('copyright');
    setDetails('');
    setSubmitted(false);
  };

  const submit = async () => {
    if (!refId) return;
    const me = await checkAuth();
    if (!me) {
      toast.error('You need to be signed in to report content.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/content/${encodeURIComponent(refId)}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, details: details.trim() }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to file report');
      }
      const d = await r.json();
      setSubmitted(true);
      if (d.duplicate) {
        toast.success('You already reported this — our team is on it.');
      } else {
        toast.success('Thanks. Our admins are reviewing this title.');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to file report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="report-content-modal"
    >
      <div
        className="bg-slate-900 border border-slate-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Flag className="w-5 h-5 text-rose-400" />
            <h2 className="text-base font-bold text-white">Report content</h2>
          </div>
          <button
            type="button"
            onClick={() => { reset(); onClose?.(); }}
            className="p-1 rounded-md hover:bg-slate-800 text-slate-400"
            data-testid="report-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {refTitle && (
          <p className="text-xs text-slate-400 mb-4">
            Reporting <span className="text-slate-200 font-semibold">"{refTitle}"</span>.
          </p>
        )}

        {submitted ? (
          <div className="py-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
            <p className="text-sm text-slate-200">Thanks for the heads-up.</p>
            <p className="text-xs text-slate-500 mt-1">
              Our admins will review this within 48 hours. You won't be notified individually unless they need more info.
            </p>
            <button
              type="button"
              onClick={() => { reset(); onClose?.(); }}
              className="mt-5 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold"
              data-testid="report-modal-done"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <fieldset className="space-y-2 mb-4">
              <legend className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">Reason</legend>
              {REASONS.map((r) => (
                <label
                  key={r.id}
                  className={`flex items-start gap-2 p-2.5 rounded-lg cursor-pointer border transition ${
                    reason === r.id
                      ? 'border-rose-400/60 bg-rose-500/10'
                      : 'border-slate-800 hover:border-slate-600'
                  }`}
                  data-testid={`report-reason-${r.id}`}
                >
                  <input
                    type="radio"
                    name="report-reason"
                    value={r.id}
                    checked={reason === r.id}
                    onChange={() => setReason(r.id)}
                    className="mt-0.5 h-4 w-4 text-rose-500 focus:ring-rose-400"
                  />
                  <span className="flex-1">
                    <span className="text-sm text-white font-semibold">{r.label}</span>
                    {r.hint && <span className="block text-[11px] text-slate-500 mt-0.5">{r.hint}</span>}
                  </span>
                </label>
              ))}
            </fieldset>

            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Details (optional)</span>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value.slice(0, 2000))}
                rows={3}
                placeholder="Anything our admins should know — links, quotes, timestamps."
                className="mt-1 w-full rounded-lg bg-slate-950 border border-slate-700 text-white text-sm p-2.5 focus:outline-none focus:ring-2 focus:ring-rose-500/40"
                data-testid="report-details-input"
              />
              <span className="block text-[10px] text-slate-500 text-right mt-0.5">{details.length} / 2000</span>
            </label>

            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={() => { reset(); onClose?.(); }}
                disabled={submitting}
                className="flex-1 px-4 py-2 rounded-lg border border-slate-700 text-slate-200 hover:bg-slate-800 text-sm font-semibold"
                data-testid="report-modal-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="flex-1 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 disabled:bg-rose-900 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5"
                data-testid="report-modal-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}
                Submit report
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportContentModal;
